import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import { ArrowLeft, ExternalLink, AlertCircle } from 'lucide-react'
import { apiService } from '../../services/api'
import { Button } from '../ui/button'
import LoadingSpinner from '../common/LoadingSpinner'

const getPreviewUrl = (site) => {
  if (site.azure_published_url) return site.azure_published_url
  const clientSlug = site.company_name?.toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'unknown'
  return `/sites/${clientSlug}/${site.slug}/`
}

const SitePreview = () => {
  const { siteId } = useParams()
  const navigate = useNavigate()
  const [iframeLoaded, setIframeLoaded] = useState(false)
  const [iframeError, setIframeError] = useState(false)

  const { data: site, isLoading, isError } = useQuery(
    ['site-preview', siteId],
    () => apiService.get(`/sites/${siteId}`).then(r => r.data),
    { staleTime: 60000, retry: 1 }
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-surface-50 dark:bg-dark-300">
        <LoadingSpinner size="lg" text="Loading preview..." />
      </div>
    )
  }

  if (isError || !site) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4 bg-surface-50 dark:bg-dark-300">
        <AlertCircle className="h-10 w-10 text-error-500" />
        <p className="text-neutral-600 dark:text-neutral-400">Failed to load site preview.</p>
        <Button variant="outline" onClick={() => navigate('/client/sites')}>Go Back</Button>
      </div>
    )
  }

  const previewUrl = getPreviewUrl(site)

  return (
    <div className="flex flex-col h-screen">
      {/* Top bar */}
      <div className="flex items-center gap-3 px-4 py-2.5 bg-white dark:bg-dark-200 border-b border-neutral-200 dark:border-neutral-700 shrink-0">
        <Button variant="ghost" size="sm" onClick={() => navigate('/client/sites')}>
          <ArrowLeft className="h-4 w-4 mr-1.5" />
          Back to Sites
        </Button>
        <div className="h-4 w-px bg-neutral-200 dark:bg-neutral-700" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">{site.name}</p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">Draft preview</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => window.open(previewUrl, '_blank')}>
          <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
          Open in new tab
        </Button>
      </div>

      {/* iframe area */}
      <div className="flex-1 relative bg-neutral-100 dark:bg-dark-400">
        {!iframeLoaded && !iframeError && (
          <div className="absolute inset-0 flex items-center justify-center">
            <LoadingSpinner size="lg" text="Loading site..." />
          </div>
        )}
        {iframeError ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
            <AlertCircle className="h-10 w-10 text-neutral-400" />
            <div className="text-center">
              <p className="text-neutral-600 dark:text-neutral-400 text-sm font-medium mb-1">Preview not available</p>
              <p className="text-neutral-500 dark:text-neutral-500 text-xs max-w-sm">This site may not have been published yet.</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => window.open(previewUrl, '_blank')}>
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
              Try opening directly
            </Button>
          </div>
        ) : (
          <iframe
            src={previewUrl}
            className="w-full h-full border-0"
            title={`Preview: ${site.name}`}
            onLoad={() => setIframeLoaded(true)}
            onError={() => setIframeError(true)}
          />
        )}
      </div>
    </div>
  )
}

export default SitePreview
